import {Fragment} from 'react'
import {usePrefersReducedMotion} from '@github-ui/use-prefers-reduced-motion'
import {useCombinedSummary} from '../mergeability/use-combined-summary'
import {ForYouItemContent, ForYouVisual} from './ForYouItemContent'
import {LegacyForYouItem} from './ForYouItemLegacy'
import {
  isCanonicalForYouItemProps,
  type CanonicalForYouItemProps,
  type ForYouItemProps as ForYouItemPropsContract,
} from './for-you-item.types'
import {isForYouCombinedSummaryEnabled, isForYouRefreshEnabled} from './for-you-feature-flags'

export type ForYouItemProps = ForYouItemPropsContract

/**
 * With the combined summary enabled, the item's title, description and visual describe the whole pull request.
 * That assumes the item is the one card the For You deciders selected; loading and error placeholders, or items
 * shown anywhere else, should render `ForYouItemPresentation` directly.
 */
export function ForYouItem(props: ForYouItemProps) {
  return isForYouCombinedSummaryEnabled() && isCanonicalForYouItemProps(props) ? (
    <ForYouItemWithCombinedSummary item={props} />
  ) : (
    <ForYouItemPresentation {...props} />
  )
}

function ForYouItemWithCombinedSummary({item}: {item: CanonicalForYouItemProps}) {
  const summary = useCombinedSummary(item.subText)
  if (!summary) return <ForYouItemPresentation {...item} />

  const {title, description, visual} = summary
  // Keep the selected item's actions, errors and analytics identity unchanged.
  return <ForYouItemPresentation {...item} title={title} subText={description} visual={visual} />
}

/** Renders an item exactly as given, without the combined summary. */
export function ForYouItemPresentation(props: ForYouItemProps) {
  if (isCanonicalForYouItemProps(props)) {
    if (props.visual === undefined) {
      throw new Error('Canonical ForYouItem requires a visual')
    }
    if ('children' in props && props.children != null) {
      throw new Error('Canonical ForYouItem does not support children')
    }
    return isForYouRefreshEnabled() ? <ForYouItemContent {...props} /> : <CanonicalLegacyForYouItem {...props} />
  }

  return <LegacyForYouItem {...props} />
}

function CanonicalLegacyForYouItem({
  itemId,
  visual,
  title,
  subText,
  actions,
  footer,
  fullWidthActionSlotOnNarrow,
}: CanonicalForYouItemProps) {
  const reducedMotion = usePrefersReducedMotion()
  const renderedActions = actions?.filter(action => action.content)

  return (
    <LegacyForYouItem
      itemName={itemId}
      title={title}
      description={subText}
      leadingVisual={<ForYouVisual visual={visual} reducedMotion={reducedMotion} />}
      emphasizedLeadingVisual
      fullWidthActionSlotOnNarrow={fullWidthActionSlotOnNarrow ? 'row' : true}
      variant="embedded"
      footer={footer}
      actionSlot={
        renderedActions?.length ? (
          <div className="d-flex flex-wrap gap-2">
            {renderedActions.map(action => (
              <Fragment key={action.id}>{action.content}</Fragment>
            ))}
          </div>
        ) : undefined
      }
    />
  )
}
